import { Group, UnstyledButton, Text, ActionIcon, Tooltip, Loader } from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';
import { MAX_OPEN_SESSIONS, useAssistantChat, useBusyReplySessionIds } from '../../../hooks/useAssistantChat';

export interface KianFlyoutTabsProps {
  onNewSession: () => void;
}

export function KianFlyoutTabs({ onNewSession }: KianFlyoutTabsProps) {
  const openSessionIds = useAssistantChat((s) => s.openSessionIds);
  const openSessionMeta = useAssistantChat((s) => s.openSessionMeta);
  const currentSessionId = useAssistantChat((s) => s.currentSessionId);
  const openSessionTab = useAssistantChat((s) => s.openSessionTab);
  const closeSessionTab = useAssistantChat((s) => s.closeSessionTab);
  const busySessionIds = useBusyReplySessionIds();

  if (openSessionIds.length <= 1) {
    return (
      <Group
        gap={6}
        justify="flex-end"
        px="xs"
        py={4}
        style={{
          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
          flexShrink: 0,
        }}
      >
        <Tooltip
          label={
            openSessionIds.length >= MAX_OPEN_SESSIONS
              ? `Maximum ${MAX_OPEN_SESSIONS} Chats gleichzeitig`
              : 'Neuer Chat'
          }
          withArrow
        >
          <ActionIcon
            size="sm"
            variant="subtle"
            color="gray"
            onClick={onNewSession}
            disabled={openSessionIds.length >= MAX_OPEN_SESSIONS}
            aria-label="Neuer Chat"
          >
            <IconPlus size={14} />
          </ActionIcon>
        </Tooltip>
      </Group>
    );
  }

  return (
    <Group
      gap={4}
      px="xs"
      py={4}
      wrap="nowrap"
      style={{
        borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
        overflowX: 'auto',
        flexShrink: 0,
      }}
    >
      {openSessionIds.map((id) => {
        const meta = openSessionMeta[id];
        const label = meta?.title || meta?.entityLabel || 'Chat';
        const active = id === currentSessionId;
        return (
          <Group
            key={id}
            gap={4}
            wrap="nowrap"
            style={{
              borderRadius: 6,
              background: active ? 'rgba(255, 102, 0, 0.15)' : 'rgba(255, 255, 255, 0.04)',
              border: active
                ? '1px solid rgba(255, 102, 0, 0.45)'
                : '1px solid rgba(255, 255, 255, 0.06)',
              padding: '2px 4px 2px 8px',
              flexShrink: 0,
              maxWidth: 180,
            }}
          >
            <UnstyledButton
              onClick={() => {
                if (id !== currentSessionId) void openSessionTab(id);
              }}
              style={{ minWidth: 0, flex: 1 }}
            >
              <Group gap={6} wrap="nowrap">
                {busySessionIds.has(id) ? (
                  <Loader size={10} color="cyberOrange" aria-label="KIAN antwortet" style={{ flexShrink: 0 }} />
                ) : null}
                <Text size="xs" fw={active ? 600 : 400} lineClamp={1} style={{ color: active ? 'var(--mantine-color-cyberOrange-3)' : undefined }}>
                  {label}
                </Text>
              </Group>
            </UnstyledButton>
            <ActionIcon
              size="xs"
              variant="subtle"
              color="gray"
              onClick={(e) => {
                e.stopPropagation();
                closeSessionTab(id);
              }}
              aria-label={`Tab ${label} schließen`}
            >
              <IconX size={12} />
            </ActionIcon>
          </Group>
        );
      })}
      <Tooltip
        label={
          openSessionIds.length >= MAX_OPEN_SESSIONS
            ? `Maximum ${MAX_OPEN_SESSIONS} Chats gleichzeitig`
            : 'Neuer Chat'
        }
        withArrow
      >
        <ActionIcon
          size="sm"
          variant="subtle"
          color="gray"
          onClick={onNewSession}
          disabled={openSessionIds.length >= MAX_OPEN_SESSIONS}
          aria-label="Neuer Chat"
        >
          <IconPlus size={14} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}
